import { useTranslation } from "react-i18next";
import type { StepPlan } from "../../../../api/content/stepPlans";
import type { StepDisplayState } from "../../../../api/threads/liveMessages";
import type { ThreadStepPlan } from "../../../../api/threads/threadsApi";
import AppButton from "../../../../ui/buttons/AppButton";
import { classNames } from "../../../../ui/styles/classNames";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { OptionalChip, StepNumber, StepStateChip } from "../../../workflows/graph/NodeControls";
import StepKindMark from "../../../workflows/graph/StepKindMark";
import { stepKindOfDefinition } from "../../../workflows/graph/stepKind";
import RunsWithCard from "../../llmChoice/RunsWithCard";
import PlannedInputsList, { type GivenValues } from "../../stepPlans/PlannedInputsList";
import { namesText } from "../../stepPlans/planText";
import StepReadiness from "../../stepPlans/StepReadiness";
import { usePromptParts } from "../../stepPlans/usePromptParts";
import type { StepInWorkflow } from "../currentStep/stepUses";
import { useThreadContext } from "../threadPageContext";
import { useRunDraft } from "../currentStep/useRunDraft";
import StepReads from "./StepReads";

interface StepRowProps {
  readonly stepKey: string;
  /** The step's number in its Workflow, as the graph numbers it; its place while the Workflow is unread. */
  readonly number: string;
  readonly step: StepInWorkflow | undefined;
  /** Its state as module C shows it (W-22). */
  readonly state: StepDisplayState | undefined;
  readonly plan: ThreadStepPlan | undefined;
  readonly plans: ReadonlyMap<string, StepPlan>;
  readonly bindings: string | null;
  readonly stepName: (stepKey: string) => string;
  /** Set when "Run to here" may run to this step: what it does. */
  readonly onRunTo: (() => void) | null;
  /** The steps a run to this step skips, as the button's title (PC-4). */
  readonly runToTitle?: string;
  /** Why this viewer may not run to it (W-22 slice 2): the button stays, unavailable, and says so. */
  readonly runToUnavailable?: string;
  /** Set for a step a run skipped: running it now is a rerun (PC-4), with the values typed in its row. */
  readonly onRun?: ((values: Readonly<Record<string, unknown>>) => void) | null;
  /** A run may not be requested now: one is being sent, or a turn runs. */
  readonly runBusy?: boolean;
  readonly busy: boolean;
  /** Opened: one row at a time beside the current step (F100, TR-B1-04). */
  readonly open: boolean;
  readonly onToggle: (open: boolean) => void;
  /** Each step it waits for as module C shows it in this Run (TR-B1-02, W-22). */
  readonly stateOf: (stepKey: string) => StepDisplayState | null;
}

const NO_CHANGE = () => undefined;
const NO_VALUES: GivenValues = {};

/**
 * A step that is not the current one, closed to one line (F100): its number, name and kind,
 * "Optional, on request" for a step someone starts, its state once it was due, and "Run to
 * here" where the Workflow can run to it. Opened, it says what it does, what it reads and
 * takes, and what it still lacks (TR-B1-04); its prompt and how each input is wired, from
 * module B's plan, fold under one detail. Looking at one runs nothing.
 */
export default function StepRow({ stepKey, number, step, state, plan, plans, bindings, stepName, onRunTo, runToTitle, runToUnavailable, onRun = null, busy, runBusy = busy, open, onToggle, stateOf }: StepRowProps) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  // A skipped step runs with what is typed in its row, as the current step does (PC-3, PC-4).
  const draft = useRunDraft(threadId, onRun === null ? null : { stepKey, position: 0, state: "ready", shown: "ready" }, overview.lastEntryNumber);
  const shownPlan = draft.plan ?? plan;
  const definitionKind = step?.step?.kind ?? null;
  const kind = definitionKind === null ? null : stepKindOfDefinition(definitionKind);
  const optional = step?.start === "on_request";
  const description = step?.step?.description ?? "";
  // A skipped step's inputs are edited for its rerun, so they stay out of the fold (PC-3, PC-4).
  const editsInputs = onRun !== null;
  return (
    <li className={classNames("pl-3", kind === null ? "" : `app-step-strip app-step-kind-${kind}`, optional ? "app-step-optional" : "")}>
      <details open={open} onToggle={(event) => { onToggle(event.currentTarget.open); }}>
        <summary className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 py-0.5 text-sm">
          <StepNumber number={number} />
          <span className="app-text">{stepName(stepKey)}</span>
          {definitionKind === null ? null : <StepKindMark kind={definitionKind} needsPerson={plan?.needsPerson === true} />}
          {optional ? <OptionalChip text={t("workflows.node.onRequest")} /> : null}
          {state === undefined || state === "upcoming" ? null : <StepStateChip state={state} />}
          {onRun === null ? null : (
            <AppButton
              variant="secondary"
              size="xs"
              className="ml-auto"
              disabled={runBusy || draft.pending}
              onClick={(event) => {
                // The button sits in the summary: run, without opening the row.
                event.preventDefault();
                onRun(draft.values);
              }}
            >
              {t("threads.current.run")}
            </AppButton>
          )}
          {onRunTo === null ? null : (
            <AppButton
              variant="secondary"
              size="xs"
              className="ml-auto"
              title={runToTitle}
              disabled={busy}
              unavailable={runToUnavailable}
              onClick={(event) => {
                // The button sits in the summary: run, without opening the row.
                event.preventDefault();
                onRunTo();
              }}
            >
              {t("threads.steps.runToHere")}
            </AppButton>
          )}
        </summary>
        {open ? (
          <div className="mt-1 space-y-1.5 pb-1 pl-1">
            {description === "" ? null : <p className="text-sm">{description}</p>}
            {step === undefined ? null : <StepReads reads={step.reads} node={step} bindings={bindings} />}
            {shownPlan === undefined ? null : (
              <>
                <RunsWithCard plan={shownPlan} />
                {shownPlan.inputs.length === 0 ? null : (
                  <p className={FORM_HELP_TEXT_CLASS}>{t("threads.steps.takes", { inputs: namesText(shownPlan.inputs.map((input) => input.label), t) })}</p>
                )}
                <StepReadiness plan={shownPlan} plans={plans} stepName={stepName} values={null} onChange={NO_CHANGE} stateOf={stateOf} />
                <OpenedInputs plan={shownPlan} edits={editsInputs ? { values: draft.values, onChange: draft.change } : null} stepName={stepName} />
              </>
            )}
          </div>
        ) : null}
      </details>
    </li>
  );
}

/**
 * An opened row's inputs: a skipped step's, edited for its rerun (`edits`, PC-3, PC-4), and its prompt and wiring
 * under one fold. An agent step's prompt and each part it fills show as its run would fill them now, with the values
 * typed here, the ones Run sends (F148).
 */
function OpenedInputs({ plan, edits, stepName }: { plan: ThreadStepPlan; edits: { values: GivenValues; onChange: (values: GivenValues) => void } | null; stepName: (stepKey: string) => string }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  const parts = usePromptParts(threadId, plan.stepKind === "agent" ? plan.stepKey : null, edits?.values ?? NO_VALUES, overview.lastEntryNumber);
  return (
    <>
      {edits === null ? null : <PlannedInputsList inputs={plan.inputs} parts={parts} stepName={stepName} values={edits.values} onChange={edits.onChange} />}
      {plan.prompt === null && (edits !== null || plan.inputs.length === 0) ? null : (
        <details>
          <summary className="app-text-muted cursor-pointer text-xs">{t("threads.steps.technical")}</summary>
          <div className="mt-2">
            <PlannedInputsList prompt={plan.prompt} parts={parts} inputs={edits === null ? plan.inputs : []} stepName={stepName} values={null} onChange={NO_CHANGE} />
          </div>
        </details>
      )}
    </>
  );
}
